import { Box, Typography } from '@mui/material'
import { ReactComponent as RequestDetails } from '../../../../images/RequestDetails.svg'
import TitleWithIcon from '../../../../components/Cards/helpers/TitleWithIcon'
import TitleWithInfo from '../../../../components/Cards/helpers/TitleWithInfo'
import UseTranslation from '../../../../hooks/UseTranslation'
import { decodeHtmlString, getFormattedDateWithDay, getFormattedDateWithoutHours } from '../../../../utils/helpers'
import AttachmentDownloader from '../../../../components/Cards/helpers/AttachmentDownloader'
import HoverTooltip from '../../../../components/PageHelpers/HoverTooltip'

const DetailsRequest = ({ details, showTitle }) => {
  const { t, language } = UseTranslation()

  const employeeName = language === 'AR' ? details?.source?.employee?.arabicName : details?.source?.employee?.englishName
  const isManagerVacationRequest = details?.type?.key === 'MANAGER_VACATION_REQUEST'
  return (
    <Box sx={styles.detailsContainer(showTitle)}>
      {showTitle && (
      <TitleWithIcon
        title={t?.['Request Details']}
        icon={<RequestDetails />}
        customStyle={styles.stackFlex}
        textVariant="heading4"
        iconSize="sm"
      />
      )}
      <Box sx={styles.detailsBox(showTitle)}>
        <Box sx={styles.detailsData}>
          {isManagerVacationRequest && details?.source?.employee && (
            <TitleWithInfo
              title={`${t?.Employee}`}
              info={(
                <Box sx={styles.employeeInfoContainer}>
                  <HoverTooltip title={employeeName}>
                    <Typography variant="h5" sx={styles.employeeName}>
                      {employeeName}
                    </Typography>
                  </HoverTooltip>
                  <Typography variant="h6" sx={styles.employeeCode}>
                    {`#${details?.source?.employee?.code}`}
                  </Typography>
                </Box>
)}
              customStyle={styles.employeeInfo}
            />
          )}
          <TitleWithInfo
            title={`${t?.['Request Type']}`}
            info={details?.type?.name}
            customStyle={styles.detailsInfo}
          />
          {details?.source?.vacation && (
            <TitleWithInfo
              title={`${t?.['Leave Type']}`}
              info={details?.source?.vacation}
              customStyle={styles.detailsInfo}
            />
          )}

          {details?.source?.requestType && (
          <TitleWithInfo
            title={`${t?.['Employee Request Type']}`}
            info={`${details?.source?.requestType}`}
            customStyle={styles.detailsInfo}
          />
          )}

          {details?.employee?.name && (
          <TitleWithInfo
            title={`${t?.Employee}`}
            info={details?.employee?.name}
            customStyle={styles.detailsInfo}
          />
          )}
          {details?.source?.description && (
          <TitleWithInfo
            title={`${t?.Description}`}
            info={decodeHtmlString(details?.source?.description)}
            customStyle={styles.detailsInfo}
          />
          )}

          {details?.source?.startDate && (
            <TitleWithInfo
              title={`${t?.['Start Date']}`}
              info={details?.source?.unitOfMeasure === 'D'
                ? getFormattedDateWithoutHours(details?.source?.startDate)
                : getFormattedDateWithDay(details?.source?.startDate)}
              customStyle={styles.detailsInfo}
            />
          )}

          {details?.source?.endDate && (
            <TitleWithInfo
              title={`${t?.['End Date']}`}
              info={details?.source?.unitOfMeasure === 'D'
                ? getFormattedDateWithoutHours(details?.source?.endDate)
                : getFormattedDateWithDay(details?.source?.endDate)}
              customStyle={styles.detailsInfo}
            />
          )}
          {details?.source?.period && (
            <TitleWithInfo
              title={`${t?.Period}`}
              info={`${details?.source?.period} ${details?.source?.unitOfMeasure === 'D' ? t?.['Day(s)'] : t?.['Hour(s)']}`}
              customStyle={styles.detailsInfo}
            />
          )}
          {details?.attachment_group_id && (
          <TitleWithInfo
            title={`${t?.Attachments}`}
            info={(
              <AttachmentDownloader
                attachments={details?.attachment_group_id}
              />
              )}
            customStyle={styles.detailsInfo}
          />
          )}

          {details?.source?.numberOfTrips?.value && (
            <TitleWithInfo
              title={`${t?.['Visa Type']}`}
              info={details?.source?.numberOfTrips?.value}
              customStyle={styles.detailsInfo}
            />
          )}

          {details?.source?.visaDurationDays !== null && (
            <TitleWithInfo
              title={`${t?.['Visa Duration Days']}`}
              info={details?.source?.visaDurationDays}
              customStyle={styles.detailsInfo}
          />
          )}

        </Box>
        <TitleWithInfo
          title={`${t?.Comment}`}
          info={decodeHtmlString(details?.source?.comment || '-- -- --')}
          customStyle={styles.commentInfo}
        />
      </Box>
    </Box>

  )
}

const baseLabelText = {
  margin: '0px',
  fontWeight: 350,
  fontSize: { xs: '13px', md: '14px' },
  lineHeight: { xs: '22px', md: '27.9px' },
}

const baseValueText = {
  margin: '0px',
  fontWeight: 350,
  fontSize: { xs: '14px', md: '14px' },
  lineHeight: '27.9px',
  overflowWrap: 'break-word',
}

export const styles = {
  detailsContainer: (showTitle) => ({
    padding: showTitle ? { xs: '10px 0px', sm: '20px' } : { xs: '0px', sm: '0px 20px' },
  }),
  stackFlex: {
    display: 'flex',
    alignItems: 'center',
    fontSize: { xs: '14px', sm: '18px' },
    gap: '10px',
    '& svg': {
      width: { xs: '15px', sm: '25px' },
      height: { xs: '15px', sm: '25px' },
    },
    '& span': {
      marginLeft: '0px',
    },
  },
  detailsBox: (showTitle) => ({
    p: showTitle ? { xs: '10px 0', md: '20px 20px 0px' } : { xs: '0px', md: '0px 20px' },
  }),

  detailsData: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
    gridAutoFlow: 'row',
    columnGap: '172px',
  },
  employeeInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', md: 'flex-start' },
    width: '100%',
    my: '10px',
    h5: {
      ...baseLabelText,
      maxWidth: { xs: '140px', md: '200px' },
      color: '#92929D',
    },
    h6: {
      ...baseValueText,
      maxWidth: { xs: '100%', md: '280px' },
    },
  },
  employeeInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: { xs: '100%', md: '100%' },
  },

  employeeName: {
    ...baseLabelText,
    maxWidth: { xs: '140px', md: '310px !important' },
    color: '#000000 !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  employeeCode: {
    ...baseValueText,
    maxWidth: { xs: '100%', md: '280px' },
    color: '#92929D',
  },

  detailsInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', md: 'center' },
    width: '100%',
    my: '10px',
    h5: {
      ...baseLabelText,
      maxWidth: { xs: '140px', md: '200px' },
      color: '#92929D',
    },
    h6: {
      ...baseValueText,
      maxWidth: { xs: '100%', md: '280px' },
    },
  },
  commentInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    my: { xs: '10px', md: '10px' },
    h5: {
      margin: '0px',
      width: '20%',
      maxWidth: { xs: '140px', md: '200px' },
      fontWeight: 350,
      fontSize: { xs: '13px', md: '14px' },
      lineHeight: { xs: '22px', md: '27.9px' },
      color: '#92929D',
    },
    h6: {
      width: '80%',
      maxWidth: { xs: '180px', md: 'unset' },
      margin: '0px',
      fontWeight: 350,
      textAlign: { xs: 'right', md: 'left' },
      fontSize: { xs: '14px', md: '14px' },
      lineHeight: '27.9px',
      overflowWrap: 'break-word',
      '& p': {
        margin: '0px',

      },
    },
  },
}

export default DetailsRequest
